import { useEffect, useState } from 'react';
import { posthogUtils, type PostHogUser } from '@/lib/posthog';
import { authModel } from '@/models/authModel';
import { ensureClient } from '@/models/util';

export function usePostHogUser() {
  const [isIdentified, setIsIdentified] = useState(false);

  useEffect(() => {
    // Skip PostHog identification in development
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    let isMounted = true;
    let identified = false;
    // The attempt in flight, set before the first await so the mount call and the SIGNED_IN event
    // auth-js raises on session recovery share one get_nav_context (PP-142).
    let pending: Promise<void> | null = null;
    // Bumped by SIGNED_OUT: an attempt started before it never identifies anyone.
    let generation = 0;

    const identifyUser = () => {
      if (identified || pending) return;
      const attempt: Promise<void> = runIdentify(generation).finally(() => {
        if (pending === attempt) pending = null;
      });
      pending = attempt;
    };

    const runIdentify = async (gen: number) => {
      try {
        const session = await authModel.getSession();
        if (!session?.user || !isMounted || gen !== generation) return;

        identified = true;
        const client = ensureClient();
        const { data, error: _error } = await client.rpc('get_nav_context', {
          p_user_id: session.user.id,
        });

        if (!isMounted || gen !== generation) return;

        const navData = data as {
          username: string | null;
          first_name: string | null;
          last_name: string | null;
          current_rating: number | null;
          gender: string | null;
          federations: Array<{ id: string }>;
          primary_location_id: string | null;
          club_id: string | null;
        } | null;

        const userData: PostHogUser = {
          id: session.user.id,
          email: session.user.email || '',
          firstName: session.user.user_metadata?.first_name || navData?.first_name || undefined,
          lastName: session.user.user_metadata?.last_name || navData?.last_name || undefined,
          username: navData?.username || undefined,
          rating: navData?.current_rating || undefined,
          gender: navData?.gender || undefined,
          federationId: navData?.federations?.[0]?.id || undefined,
          locationId: navData?.primary_location_id || undefined,
          clubId: navData?.club_id || undefined,
        };

        posthogUtils.identifyUser(userData);

        if (isMounted) {
          setIsIdentified(true);
        }
      } catch (error) {
        console.error('Failed to identify user in PostHog:', error);
      }
    };

    identifyUser();

    const client = ensureClient();
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      if (event === 'SIGNED_IN' && session?.user) {
        identifyUser();
      } else if (event === 'SIGNED_OUT') {
        generation += 1;
        pending = null;
        identified = false;
        posthogUtils.resetUser();
        if (isMounted) {
          setIsIdentified(false);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return {
    isIdentified,
    identifyUser: posthogUtils.identifyUser,
    resetUser: posthogUtils.resetUser,
    setUserProperty: posthogUtils.setUserProperty,
    getCurrentUserId: posthogUtils.getCurrentUserId,
  };
}
